import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Flag, Loader2, MapPin, Plus, Settings2, Trash2, Trophy, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import DateField from '../components/DateField';
import { VenuePicker, type PickedVenue } from '../components/VenuePicker';
import { trophiesService } from '../../services/trophies';
import { eventsService } from '../../services/events';
import { useGymAdmin } from '../context/gym-admin-context';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import type { Stage } from '../../types/trophy';
import { competitionBadge } from '../../utils/lifecycle-status';

interface StageForm {
  name: string;
  startDate: string;
  endDate: string;
  venue: PickedVenue | null;
}

const EMPTY_FORM: StageForm = { name: '', startDate: '', endDate: '', venue: null };

/**
 * The two places this list is kept, and how each reads it. A trophy's stages
 * and a gym's events are the same record — a competition — seen from the two
 * places that can stand behind one; the list, the card and the dialog are
 * shared, and only the words and the endpoints differ.
 */
const SIDES = {
  trophy: {
    copy: 'gymAdmin.stages',
    list: (slug: string) => trophiesService.listStages(slug),
    create: (slug: string, form: StageForm) =>
      trophiesService.createStage(slug, {
        name: form.name.trim(),
        start_date: form.startDate,
        end_date: form.endDate,
        venue_id: form.venue?.id ?? null,
      }),
    remove: (slug: string, id: string) => trophiesService.deleteStage(slug, id),
    // Naming a gym hands it the stage; a gym making its own event is the gym.
    asksVenue: true,
  },
  gym: {
    copy: 'gymAdmin.events',
    list: (slug: string) => eventsService.list(slug),
    create: (slug: string, form: StageForm) =>
      eventsService.create(slug, {
        name: form.name.trim(),
        start_date: form.startDate,
        end_date: form.endDate,
      }),
    remove: (slug: string, id: string) => eventsService.delete(slug, id),
    asksVenue: false,
  },
} as const;

/**
 * The circuit's stages, or the gym's events: what an admin of either comes
 * here to do.
 *
 * "Manage" switches the sidebar to the competition rather than opening a
 * sub-page. A competition is a place in its own right with its own staff, its
 * own entry list and its rounds — putting it behind the switcher is what makes
 * all of those sections work on it unchanged, instead of growing a second copy
 * of each nested under the trophy or the gym.
 */
export default function StagesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { selectGym, locationType } = useGymAdmin();
  const { location: owner, loading: ownerLoading, slug } = useCurrentLocation();
  const side = locationType === 'gym' ? SIDES.gym : SIDES.trophy;
  const copy = side.copy;

  const [stages, setStages] = useState<Stage[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<StageForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(
    async (ownerSlug: string) => {
      setListLoading(true);
      try {
        setStages(await side.list(ownerSlug));
      } catch (error) {
        toast({
          title: t(`${copy}.loadError`),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast, side, copy]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  const handleCreate = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const stage = await side.create(slug, form);
      setStages((current) => [...current, stage]);
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      toast({ title: t(`${copy}.created`) });
    } catch (error) {
      toast({
        title: t(`${copy}.saveError`),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (stage: Stage) => {
    if (!slug) return;
    setDeletingId(stage.id);
    try {
      await side.remove(slug, stage.id);
      setStages((current) => current.filter((s) => s.id !== stage.id));
      toast({ title: t(`${copy}.deleted`) });
    } catch (error) {
      // The server refuses a stage somebody has entered, and says so — that
      // message is more use here than a generic failure.
      toast({
        title: t(`${copy}.deleteError`),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  const canSubmit = form.name.trim().length > 0 && !!form.startDate && !!form.endDate;

  if (ownerLoading) return <Skeleton className="h-96 w-full max-w-5xl" />;
  if (!owner || !slug) return null;

  return (
    <div className="max-w-5xl space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>{t(`${copy}.title`)}</CardTitle>
            <CardDescription>{t(`${copy}.description`)}</CardDescription>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="gap-2 shrink-0">
            <Plus className="size-4" />
            {t(`${copy}.add`)}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {listLoading && <Skeleton className="h-24 w-full" />}

          {!listLoading && stages.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t(`${copy}.empty`)}
            </p>
          )}

          {!listLoading &&
            stages.map((stage) => {
              // On a gym's list, a stage it hosts is the circuit's: it can be
              // run from here, not deleted.
              const hostedStage = !side.asksVenue && !!stage.trophy;
              return (
                <div
                  key={stage.id}
                  className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                  data-testid="stage-row"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <Flag className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="truncate font-medium">{stage.name}</span>
                      <Badge variant={competitionBadge(stage.status, stage.entries_open).variant}>
                        {t(competitionBadge(stage.status, stage.entries_open).labelKey)}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {stage.start_date && (
                        <span className="flex items-center gap-1">
                          <CalendarDays className="size-3" aria-hidden />
                          {formatDate(stage.start_date)}
                          {stage.end_date && ` — ${formatDate(stage.end_date)}`}
                        </span>
                      )}
                      {side.asksVenue && stage.venue && (
                        <span className="flex items-center gap-1">
                          <MapPin className="size-3" aria-hidden />
                          {stage.venue.name}
                        </span>
                      )}
                      {hostedStage && (
                        <span className="flex items-center gap-1" data-testid="hosted-stage">
                          <Trophy className="size-3" aria-hidden />
                          {t('gymAdmin.events.stageOf', { trophy: stage.trophy?.name })}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="size-3" aria-hidden />
                        {t('gymAdmin.stages.entrants', { count: stage.participants_count })}
                      </span>
                    </div>
                  </div>

                  <div className="flex shrink-0 gap-2">
                    {/* Switching the sidebar, not navigating: the competition is
                        its own place, with the whole menu that comes with one. */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => selectGym(stage.slug)}
                    >
                      <Settings2 className="size-4" />
                      {t('gymAdmin.stages.manage')}
                    </Button>
                    {!hostedStage && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(stage)}
                        disabled={deletingId === stage.id}
                        title={t(`${copy}.delete`)}
                      >
                        {deletingId === stage.id ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Trash2 className="size-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`${copy}.add`)}</DialogTitle>
            {/* The code and the country are not asked for: the server takes
                them from the circuit or the gym, or from the venue once one
                is set. */}
            <DialogDescription>{t(`${copy}.addDescription`)}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stage-name">{t('gymAdmin.stages.name')}</Label>
              <Input
                id="stage-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t(`${copy}.namePlaceholder`)}
              />
            </div>
            {/* Each date bounds the other: a one-day event is the same day
                twice, and an end before the start cannot be picked at all. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <DateField
                id="stage-start"
                label={t('gymAdmin.stages.startDate')}
                value={form.startDate}
                max={form.endDate}
                onChange={(value) => setForm((f) => ({ ...f, startDate: value }))}
              />
              <DateField
                id="stage-end"
                label={t('gymAdmin.stages.endDate')}
                value={form.endDate}
                min={form.startDate}
                onChange={(value) => setForm((f) => ({ ...f, endDate: value }))}
              />
            </div>
            {side.asksVenue && (
              <VenuePicker value={form.venue} onChange={(venue) => setForm((f) => ({ ...f, venue }))} />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={!canSubmit || saving}>
              {saving ? t('gymAdmin.saving') : t(`${copy}.create`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
